import { FormArray, FormControl, FormGroup } from '@angular/forms';

// ============================================================================
// ENUMS - Must be defined first as they're used in interfaces below
// ============================================================================

export enum PartnerImportStatus {
  Synced = 'Synced',
  Orphaned = 'Orphaned',
  HasConflicts = 'HasConflicts',
}

export enum PartnerUpdateStatus {
  Pristine = 'Pristine',
  Touched = 'Touched',
}

export enum IntegrationType {
  Unknown = 'Unknown',
  Helsi = 'Helsi',
  Eleks = 'Eleks',
  Dila = 'Dila',
  Dobrobut = 'Dobrobut',
  None = 'None',
  Pharmacy = 'Pharmacy',
}

export enum PartnerType {
  Clinic = 'Clinic',
  Laboratory = 'Laboratory',
  Pharmacy = 'Pharmacy',
}

export enum PartnerStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum PartnerAvailability {
  Available = 'Available',
  Unavailable = 'Unavailable',
}

export enum LocationStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum LocationAvailability {
  Available = 'Available',
  Unavailable = 'Unavailable',
}

export enum ServiceType {
  Generic = 'Generic',
  Pharmacy = 'Pharmacy',
  Laboratory = 'Laboratory',
  Appointment = 'Appointment',
}

// ============================================================================
// SUPPORTING INTERFACES - Shared types used across entities
// ============================================================================

export interface Contact {
  name: string;
  type: string;
  contactDetails: Partial<ContactDetails>;
  location: Partial<PhysicalAddress>;
}

export interface PhysicalAddress {
  city: string;
  address: string;
  coordinates: Partial<{
    latitude: number;
    longitude: number;
  }>;
}

export interface ContactDetails {
  phoneNumber: string;
  email: string;
}

export interface DepartmentSchedule {
  dayOfWeek: string;
  from: string;
  to: string;
  formattedHours: string;
}

export interface ScheduleUiModel {
  daysOfWeek: string[];
  from: string;
  to: string;
  formattedHours: string;
}

export interface ScheduleDisplayModel {
  label: string;
  time: string;
}

export interface DynamicContentSection {
  title: string;
  type: string;
  elements: string[];
}

export interface OfferingInstructions {
  items: OfferingInstructionItem[];
}

export interface OfferingInstructionItem {
  id: string;
  type: string;
  description: string;
  value: number;
}

// ============================================================================
// RESPONSE INTERFACES - API response types
// ============================================================================

export interface BasePartnerEntityResponse {
  id: string;
  active: boolean;
  available: boolean;
  importStatus: PartnerImportStatus;
  updateStatus: PartnerUpdateStatus;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  deletedAt: string;
  deletedBy: string;
}

export interface PartnerResponse extends BasePartnerEntityResponse {
  name: string;
  logoUrl?: string;
  integrationEnabled: boolean;
  integration: IntegrationType;
  contacts: Contact[];
}

export interface LocationResponse extends BasePartnerEntityResponse {
  partnerId: string;
  partnerName: string;
  name: string;
  logoUrl: string;
  integrationEnabled: boolean;
  integration: IntegrationType;
  contacts: Contact[];
  location: PhysicalAddress;
  schedule: DepartmentSchedule[];
}

export interface DoctorResponse extends BasePartnerEntityResponse {
  partnerId: string;
  partnerExternalId: string;
  fullName: string;
  email: string;
  phone: string;
  specialization: string;
  specializations: string[];
  photoUrl: string;
  originalPhotoHash: string;
  presentationVideoPath: string;
  dynamicContentSections: DynamicContentSection[];
  experienceInYears: number;
  isEnglishSpeaking: boolean;
}

export interface OfferingResponse extends BasePartnerEntityResponse {
  $type: number;
  externalId: string;
  externalCode: string;
  partnerId: string;
  serviceType: string;
  name: string;
  description: string;
  code: string;
  price: number;
  currency: string;
  instructionsRaw: string;
  instructions: OfferingInstructions;
  integration: IntegrationType;
  canBeOrderedOnline: boolean;
  canProduceMedicalData: boolean;
  allowedForGenders: string[];
  minAge: number;
  maxAge: number;
}

// ============================================================================
// V2 INTERFACES - Domain model types
// ============================================================================

export interface PartnerV2 {
  id: string;
  logoUrl: string;
  active: boolean;
  available: boolean;
  contacts: Contact[];
  integrationEnabled: boolean;
  importStatus: PartnerImportStatus;
  integration: IntegrationType;
  name: string;
  updateStatus: PartnerUpdateStatus;
}

export interface LocationV2 {
  partnerId: string;
  partnerName: string;
  logoUrl?: string;
  active: boolean;
  available: boolean;
  contacts: Contact[];
  integrationEnabled: boolean;
  location: PhysicalAddress;
  id: string;
  importStatus: PartnerImportStatus;
  integration: IntegrationType;
  name: string;
  updateStatus: PartnerUpdateStatus;
  schedule: DepartmentSchedule[];
}

export interface DoctorV2 {
  id: string;
  fullName: string;
  integrationEnabled: boolean;
  importStatus: PartnerImportStatus;
  integration: IntegrationType;
  partnerId: string;
  active: boolean;
  available: boolean;
  updateStatus: PartnerUpdateStatus;
  photoUrl: string;
  specialization: string;
  specializations: string[];
}

export interface OfferingV2 {
  $type: number;
  id: string;
  serviceType: string;
  name: string;
  description: string;
  code: string;
  externalId: string;
  externalCode: string;
  price: number;
  currency: string;
  integrationEnabled: boolean;
  importStatus: PartnerImportStatus;
  integration: IntegrationType;
  active: boolean;
  available: boolean;
  partnerId: string;
  updateStatus: PartnerUpdateStatus;
}

// ============================================================================
// PAYLOAD INTERFACES - Request body types
// ============================================================================

export interface PartnerPayloadV2 {
  id?: string;
  integrationEnabled?: boolean;
  integration?: IntegrationType;
  fileName?: string;
  name: string;
  active: boolean;
  available: boolean;
  contacts: Partial<Contact>[];
}

export interface LocationPayloadV2 {
  id?: string;
  logoUrl?: string;
  partnerId: string;
  name: string;
  active: boolean;
  available: boolean;
  contacts: Partial<Contact>[];
  schedule: Partial<DepartmentSchedule>[];
  location: Partial<PhysicalAddress>;
}

export interface DoctorPayloadV2 {
  id?: string;
  partnerId: string;
  fullName: string;
  active?: boolean;
  available?: boolean;
}

export interface OfferingPayloadV2 {
  $type: number;
  id?: string;
  partnerId: string;
  serviceType: string;
  name: string;
  description?: string;
  externalId: string;
  externalCode: string;
  code?: string;
  price?: number;
  currency?: string;
  active: boolean;
  available: boolean;
}

// ============================================================================
// SEARCH MODEL INTERFACES - Query/filter types
// ============================================================================

export interface BasePartnerSearchModel {
  pageSize?: number;
  searchTerm?: string;
  integrationEnabled?: boolean;
  active?: boolean;
  available?: boolean;
  importStatus?: string;
  continuationToken?: string;
}

export interface PartnerSearchModelV2 extends BasePartnerSearchModel {}

export interface PartnerScopedSearchModelV2 extends BasePartnerSearchModel {
  partnerId: string;
}

export interface LocationSearchModelV2 extends PartnerScopedSearchModelV2 {
  partnerId: string;
}

export interface DoctorSearchModelV2 extends PartnerScopedSearchModelV2 {
  partnerId: string;
}

export interface OfferingSearchModelV2 extends PartnerScopedSearchModelV2 {
  partnerId: string;
  mappedOnly?: boolean;
}

// ============================================================================
// FORM CONTROL INTERFACES - Angular Reactive Forms types
// ============================================================================

export interface PartnerControls {
  id?: FormControl<string | null>;
  integrationEnabled?: FormControl<boolean | null>;
  name: FormControl<string | null>;
  active: FormControl<boolean | null>;
  available: FormControl<boolean | null>;
  contacts: FormArray<FormGroup<ContactControls>>;
}

export interface LocationControls {
  id?: FormControl<string | null>;
  integrationEnabled?: FormControl<boolean | null>;
  logoUrl: FormControl<string | null>;
  name: FormControl<string | null>;
  active: FormControl<boolean | null>;
  available: FormControl<boolean | null>;
  contacts: FormArray<FormGroup<ContactControls>>;
  partnerId?: FormControl<string | null>;
  logoFileName?: FormControl<string | null>;
  location: FormGroup<{
    city: FormControl<string | null>;
    address: FormControl<string | null>;
    coordinates: FormGroup<{
      latitude: FormControl<number | null>;
      longitude: FormControl<number | null>;
    }>;
  }>;
  schedule: FormArray<FormGroup<DepartmentScheduleControls>>;
}

export interface DoctorControls {
  id?: FormControl<string | null>;
  fullName: FormControl<string | null>;
  partnerId?: FormControl<string | null>;
}

export interface OfferingControls {
  id?: FormControl<string | null>;
  serviceType?: FormControl<string | null>;
  name: FormControl<string | null>;
  description?: FormControl<string | null>;
  externalId: FormControl<string | null>;
  externalCode?: FormControl<string | null>;
  code?: FormControl<string | null>;
  price?: FormControl<number | null>;
  currency?: FormControl<string | null>;
  partnerId?: FormControl<string | null>;
}

export interface ContactControls {
  name: FormControl<string | null>;
  type: FormControl<string | null>;
  contactDetails: FormGroup<{
    phoneNumber: FormControl<string | null>;
    email: FormControl<string | null>;
  }>;
  location: FormGroup<{
    city: FormControl<string | null>;
    address: FormControl<string | null>;
    coordinates: FormControl<{ latitude: number; longitude: number } | null>;
  }>;
}

export interface DepartmentScheduleControls {
  daysOfWeek: FormControl<string[] | null>;
  from: FormControl<string | null>;
  to: FormControl<string | null>;
  formattedHours?: FormControl<string | null>;
}
